import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useSWRConfig } from "swr";
import { useDealContext } from "~/context/DealContext";
import { useDealTodos } from "~/hooks/deals/useDealTodos";
import { dealCacheKey } from "~/hooks/useDeals";
import { useTodoTemplates } from "~/hooks/useTodoTemplates";
import { PARTIALLY_FINISHED } from "~/models/Status";
import type { SurveyAnswer } from "~/models/Survey";
import {
	countUncreatedByTemplates,
	filterTodoTemplatesByStatus
} from "~/models/TodoTemplate";
import { isSuccess } from "~/utils/api/api_handler";
import { update as updateStatus } from "~/utils/api/DealStatus";
import { StatusHandleReasonButtons } from "./StatusHandleReasonButtons";

type Props = {
	alertText?: string | null;
	allowPartiallyFinish?: boolean;
	forwardDisabled?: boolean;
	nextStatusId: number;
	partiallyFinishDisabled?: boolean;
	prevStatusId?: number | null;
};

export function StatusHandleClientButtons({
	nextStatusId,
	prevStatusId = null,
	forwardDisabled = false,
	alertText = null,
	allowPartiallyFinish = false,
	partiallyFinishDisabled = false
}: Props) {
	const { notify } = useNotification();
	const deal = useDealContext();
	const { mutate: globalMutate } = useSWRConfig();
	const dealTodos = useDealTodos(deal.id);
	const todoTemplates = useTodoTemplates().useIndex().data;
	if (dealTodos.data === null || todoTemplates === null) {
		return null;
	}
	const uncreatedTodoCount = countUncreatedByTemplates(
		dealTodos.data,
		filterTodoTemplatesByStatus(deal, nextStatusId, todoTemplates)
	);
	const handleUpdateStatus = async (
		statusId: number,
		reason: string | null,
		surveyAnswers: SurveyAnswer[] | null
	): Promise<boolean> => {
		const result = await updateStatus(deal.id, statusId, reason, surveyAnswers);
		if (isSuccess(result)) {
			await globalMutate(dealCacheKey(deal.id));
			notify({ content: "更新しました。", variant: "success" });
			return true;
		}
		notify({
			content: result.message ?? "エラーが発生しました。",
			variant: "error"
		});
		return false;
	};
	const handleForwardStatus = async (): Promise<void> => {
		const succeeded = await handleUpdateStatus(nextStatusId, null, null);
		if (succeeded) {
			await dealTodos.mutate();
			if (uncreatedTodoCount !== 0) {
				notify({
					content: `${uncreatedTodoCount}件のTODOを追加しました。`,
					variant: "success"
				});
			}
		}
	};
	const handleBackwardStatus = async (reason: string): Promise<void> => {
		if (prevStatusId !== null) {
			await handleUpdateStatus(prevStatusId, reason, null);
		}
	};
	const handleForwardStatusWithSurvey = async (
		surveyAnswers: SurveyAnswer[] | null
	): Promise<void> => {
		await handleUpdateStatus(nextStatusId, null, surveyAnswers);
	};
	const handlePartiallyFinish = async (): Promise<void> => {
		await handleUpdateStatus(PARTIALLY_FINISHED, null, null);
	};

	return (
		<StatusHandleReasonButtons
			alertText={alertText}
			canBackward={prevStatusId !== null}
			deal={deal}
			forwardDisabled={forwardDisabled}
			handleBackwardStatus={handleBackwardStatus}
			handleForwardStatus={handleForwardStatus}
			handleForwardStatusWithSurvey={handleForwardStatusWithSurvey}
			handlePartiallyFinish={
				allowPartiallyFinish ? handlePartiallyFinish : null
			}
			nextStatusId={nextStatusId}
			partiallyFinishDisabled={partiallyFinishDisabled}
		/>
	);
}
